import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { gameAPI } from "../utils/api";
import {
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Alert,
  Button,
  Tabs,
  Tab,
  Badge,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api } from "@/utils/apiService";
import { toast } from "react-toastify";
import PaymentOptions from "../checkout/PaymentOptions";

const GameDetail = () => {
  const { theme } = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("packs");
  const [userId, setUserId] = useState("");
  const [productId, setProductId] = useState("");
  const [server, setServer] = useState("");
  const [serverRegion, setServerRegion] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("razorpay");
  const [upiMethod, setUpiMethod] = useState("qr");

  const handlePaymentChange = (method, data = {}) => {
    setPaymentMethod(method);
    if (method === "matrixsols" && data.method) {
      setUpiMethod(data.method);
    }
  };

  // console.log(game)

  useEffect(() => {
    const fetchGame = async () => {
      try {
        setLoading(true);
        const data = await gameAPI.getGameById(id);
        setGame(data.game);
        setProductId(data.game?.packs?.[0]?.productId || "");
      } catch (err) {
        setError("Failed to load game details. Please try again later.");
        console.error("Error fetching game:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchGame();
  }, [id]);

  const handleValidateUser = async (e) => {
    e.preventDefault();
    if (!token) {
      setError("Please log in to validate your user ID.");
      return;
    }
    if (!userId.trim()) {
      setError("Please enter a valid User ID.");
      return;
    }

    setIsValidating(true);
    setError("");
    try {
      const resolvedProductId = productId || game?.packs?.[0]?.productId;
      const formData = {
        playerId: userId,
        gameId: game._id,
        ...(resolvedProductId ? { productId: resolvedProductId } : {}),
        ...(server ? { server } : {}),
        ...(serverRegion ? { serverRegion } : {}),
      };

      const res = await api.post("/orders/verify-game-id", formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      console.log(res);
      if (res.data.verified) {
        setValidationResult({
          success: res.data.success,
          verified: res.data.verified,
          message: res.data.message,
          data: res.data.data,
        });
        toast.success(res.data.message);
      } else {
        setValidationResult({
          success: res.data.success,
          verified: res.data.verified,
          message: res.data.message,
          data: res.data.data || null,
        });
        toast.error(res.data.message);
      }
    } catch (error) {
      console.log("Error Validating User ::", error);
      const mssg = error?.response?.data?.message || "Error Validating User";
      toast.error(mssg);
      setValidationResult(null);
    } finally {
      setIsValidating(false);
    }
  };

  const handlePurchase = async (pack) => {
    if (!token) {
      toast.error("Please log in to purchase a pack.");
      return;
    }

    if (!userId.trim()) {
      toast.error("Please enter your User ID before purchasing.");
      return;
    }

    if (!pack?.packId) {
      toast.error("Selected pack is missing an ID.");
      return;
    }

    setIsPurchasing(pack.packId || pack._id);

    try {
      const payload = {
        gameId: game._id,
        packId: pack.packId,
        accountId: userId.trim(),
        quantity: 1,
        paymentMethod,
        method: paymentMethod === "matrixsols" ? upiMethod : undefined,
        contact: user?.phone || user?.email || "",
        ...(server ? { serverId: server } : {}),
      };

      const res = await api.post("/orders", payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (res.data?.success) {
        toast.info("Redirecting to payment gateway...");
        const redirectUrl =
          res.data?.paymentInfo?.redirectUrl ||
          res.data?.paymentInfo?.paymentUrl;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      } else {
        toast.error(res.data?.message || "Failed to create order.");
      }
    } catch (error) {
      const mssg = error?.response?.data?.message || "Failed to create order.";
      toast.error(mssg);
    } finally {
      setIsPurchasing(null);
    }
  };

  if (loading) {
    return (
      <Container className="text-center my-5" data-bs-theme={theme}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="my-5" data-bs-theme={theme}>
        <Alert variant="danger">{error}</Alert>
        <Button variant="secondary" onClick={() => navigate(-1)}>
          Back to Games
        </Button>
      </Container>
    );
  }

  if (!game) {
    return (
      <Container className="my-5" data-bs-theme={theme}>
        <Alert variant="warning">Game not found</Alert>
        <Button variant="secondary" onClick={() => navigate("/games")}>
          Back to Games
        </Button>
      </Container>
    );
  }

  return (
    <Container className="my-5" data-bs-theme={theme}>
      <Button
        variant="outline-secondary"
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        ← Back to Games
      </Button>

      <Row>
        <Col md={4}>
          <Card className="mb-4">
            <Card.Img
              variant="top"
              src={game.image || "/images/default-game.jpg"}
              alt={game.name}
              style={{ height: "300px", objectFit: "cover" }}
            />
            <Card.Body>
              <Card.Title>{game.name}</Card.Title>
              <Card.Subtitle className="mb-2 text-muted">
                {game.category} • {game.region}
              </Card.Subtitle>
              <div className="d-flex gap-2 mb-3">
                <Badge bg="primary">{game.packs?.length || 0} Packs</Badge>
                <Badge bg="success">
                  {game.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
              <Card.Text>{game.description}</Card.Text>
            </Card.Body>
          </Card>
        </Col>

        <Col md={8}>
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-4"
            id="game-tabs"
          >
            <Tab eventKey="packs" title="Game Packs">
              <div className="mt-4">
                <h4>Available Packs</h4>
                <div className="mb-3">
                  <label htmlFor="UserId" className="form-label">
                    Your Game User ID
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="UserId"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                    placeholder="Enter your in-game User ID"
                  />
                </div>


                <div className="mb-3">
                  <PaymentOptions
                    selectedMethod={paymentMethod}
                    onPaymentMethodChange={handlePaymentChange}
                  />
                </div>
                {game.packs?.length > 0 ? (
                  <Row xs={1} md={2} className="g-4">
                    {game.packs.map((pack) => (
                      <Col key={pack._id || pack.packId}>
                        <Card>
                          <Card.Body>
                            <Card.Title>{pack.name}</Card.Title>
                            <Card.Text>
                              <strong>Amount:</strong> {pack.amount}
                              <br />
                              <strong>Price:</strong>Rs.
                              {pack.retailPrice?.toFixed(2)}
                            </Card.Text>
                            <Button
                              variant="primary"
                              onClick={() => handlePurchase(pack)}
                              disabled={
                                isPurchasing === (pack.packId || pack._id)
                              }
                            >
                              {isPurchasing === (pack.packId || pack._id)
                                ? "Processing..."
                                : "Purchase"}
                            </Button>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  <Alert variant="info">
                    No packs available for this game.
                  </Alert>
                )}
              </div>
            </Tab>

            <Tab eventKey="validate" title="Validate User">
              <div className="mt-4">
                <h4>Validate Game Account</h4>
                <p className="text-muted mb-4">
                  Enter your in-game user ID to validate your account and view
                  available top-up options.
                </p>
                {error && <Alert variant="danger">{error}</Alert>}{" "}
                {/* Display errors prominently */}
                <form onSubmit={handleValidateUser}>
                  <div className="mb-3">
                    <label htmlFor="userId" className="form-label">
                      Your Game User ID
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="userId"
                      value={userId}
                      onChange={(e) => setUserId(e.target.value)}
                      required
                      disabled={isValidating} // Disable during validation
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="productId" className="form-label">
                      Game Code (Required)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="productId"
                      value={productId}
                      onChange={(e) => setProductId(e.target.value)}
                      required
                      disabled={isValidating}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="server" className="form-label">
                      Server (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="server"
                      value={server}
                      onChange={(e) => setServer(e.target.value)}
                      disabled={isValidating}
                    />
                  </div>

                  <div className="mb-3">
                    <label htmlFor="serverRegion" className="form-label">
                      Server Region (Optional)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="serverRegion"
                      value={serverRegion}
                      onChange={(e) => setServerRegion(e.target.value)}
                      disabled={isValidating}
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isValidating || !userId.trim()} // Prevent empty submissions
                    className="me-2"
                  >
                    {isValidating ? "Validating..." : "Validate"}
                  </Button>

                  {validationResult && (
                    <div
                      className={`mt-3 alert ${validationResult.verified ? "alert-success" : "alert-warning"}`}
                    >
                      {validationResult.message}
                      {validationResult.data && (
                        <div className="mt-2">
                          <p>
                            <strong>Username:</strong>{" "}
                            {validationResult.data.username ||
                              validationResult.data.nickname ||
                              "Unknown"}
                          </p>
                          {(validationResult.data.region ||
                            validationResult.data.zone ||
                            validationResult.data.serverRegion ||
                            validationResult.data.server) && (
                              <p>
                                <strong>Region/Server:</strong>{" "}
                                {validationResult.data.region ||
                                  validationResult.data.zone ||
                                  validationResult.data.serverRegion ||
                                  validationResult.data.server}
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </form>
              </div>
            </Tab>
          </Tabs>
        </Col>
      </Row>

      {user?.role === "admin" && (
        <div className="mt-4 pt-3 border-top">
          <h4>Admin Actions</h4>
          <div className="d-flex gap-2">
            <Button
              variant="outline-primary"
              onClick={() => navigate(`/admin/games/edit/${id}`)}
            >
              Edit Game
            </Button>
            <Button
              variant="outline-danger"
              onClick={() => alert("Delete functionality not implemented yet.")}
            >
              Delete Game
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default GameDetail;
